<template>
  <div v-if="editable">
    <div class="w-full flex items-center gap-[16px] flex-wrap">
      <div v-if="croppedImage" class="flex items-center gap-[4px]">
        <div class="BodyS break-all">
          {{ `ขนาดรูป : ${currentFileSizeMB} MB / 100 MB` }}
        </div>
        <div
          @click="removeImage"
          class="w-[32px] h-[32px] flex items-center justify-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
          >
            <path
              d="M22.3236 11.325C22.7793 10.8693 22.7793 10.1306 22.3236 9.67498C21.868 9.21934 21.1293 9.21934 20.6737 9.67498L15.9987 14.3499L11.3238 9.67498C10.8681 9.21935 10.1294 9.21934 9.67377 9.67498C9.21814 10.1306 9.21814 10.8693 9.67377 11.325L14.3487 15.9999L9.67375 20.6749C9.21812 21.1305 9.21812 21.8692 9.67375 22.3249C10.1294 22.7805 10.8681 22.7805 11.3237 22.3249L15.9987 17.6499L20.6737 22.3249C21.1293 22.7805 21.868 22.7805 22.3237 22.3249C22.7793 21.8692 22.7793 21.1305 22.3237 20.6749L17.6487 15.9999L22.3236 11.325Z"
              fill="#EB5554"
            />
          </svg>
        </div>
      </div>
      <div v-else class="BodyS">
        รูป .jpg .jpeg หรือ .png ขนาดไม่เกิน 100 mb เท่านั้น
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleFileChange"
      />
      <div class="flex items-center gap-[16px] flex-wrap">
        <ButtonComponent
          :label="'อัปโหลดรูป'"
          :bClass="'btn_shadow--1 w-full sm:w-fit min-w-[92px]'"
          :dropdown="true"
          :dropdownList="[{ label: 'กล้องถ่ายรูป' }, { label: 'อัปโหลดรูป' }]"
          @select="({ item }) => doAction(item)"
        />
      </div>
    </div>

    <div
      v-if="showCropper"
      class="fixed inset-0 bg-black/60 z-50 flex justify-center items-center"
    >
      <div
        class="w-[90%] h-[500px] flex flex-col justify-center gap-[22px] bg-white p-4 rounded-[6px] shadow-lg max-w-[550px]"
      >
        <Cropper
          ref="cropperRef"
          class="h-[396px] bg-black w-full"
          :src="imagePreview"
          :stencil-props="stencilProps"
        />
        <div class="flex items-center justify-center gap-[10px]">
          <ButtonComponent
            label="ยกเลิก"
            :bClass="'btn_shadow--cancel'"
            @clicked="showCropper = false"
          />
          <ButtonComponent
            label="ตกลง"
            :bClass="'btn_shadow--save'"
            @clicked="cropImage"
          />
        </div>
      </div>
    </div>
    
    <div
      v-if="showCamera"
      class="fixed inset-0 bg-black/60 z-50 flex justify-center items-center"
    >
      <div
        class="w-[90%] h-[500px] flex flex-col justify-center gap-[22px] bg-white p-4 rounded-[6px] shadow-lg max-w-[550px]"
      >
        <video ref="videoRef" autoplay class="h-[396px] bg-black w-full"></video>

        <div class="flex items-center justify-center gap-[10px]">
          <ButtonComponent
            label="ยกเลิก"
            :bClass="'btn_shadow--cancel'"
            @clicked="closeCamera"
          />
          <ButtonComponent
            label="ถ่ายรูป"
            :bClass="'btn_shadow--save'"
            @clicked="takePicture"
          />
        </div>
      </div>
    </div>
    
  </div>
  <ButtonComponent
    v-else
    :label="'รูปประกอบ'"
    :bClass="'btn_shadow--cancel !px-[15px] !max-w-[90px]'"
    :lClass="'!text-[13px]'"
    @clicked="viewImage"
  />
  <ImageModal ref="ImageModalRef" />
  <StatusModal ref="StatusModalRefForUploadImage" />
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue"; // เพิ่ม nextTick
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

const props = defineProps({
  editable: {
    type: Boolean,
    default: true,
  },
  image: {
    type: [String, Number],
    default: "",
  },
  fileSizeMB: {
    type: [String, Number],
    default: "0.00",
  },
});
const emit = defineEmits(["update:image"]);

// refs
const cropperRef = ref(null);
const showCropper = ref(false);
const fileInput = ref(null);
const imagePreview = ref("");

// ✅ สถานะสำหรับขนาดไฟล์ปัจจุบัน (ใช้ ref ภายใน)
const currentFileSizeMB = ref("0.00"); 

const croppedImage = ref(null);
const rawImage = ref(null);

// --- Refs สำหรับ Camera ---
const showCamera = ref(false); // ควบคุมการแสดง Camera Modal
const videoRef = ref(null); // ref สำหรับองค์ประกอบ <video>
let mediaStream = null; // เก็บ stream กล้อง
const facingMode = ref("environment"); // 'user' หรือ 'environment'

// modal
const ImageModalRef = ref(null);
const StatusModalRefForUploadImage = ref(null);
// modal

// stencil options
const stencilProps = ref({
  aspectRatio: 1,
  movable: true,
  scalable: true,
  resizable: true,
});

// sync external image
watch(
  () => props.image,
  (val) => {
    if (typeof val === "string") {
      croppedImage.value = val;
    }
  },
  { immediate: true }
);

// ✅ ซิงค์ขนาดไฟล์เริ่มต้นจาก Props
watch(
  () => props.fileSizeMB,
  (val) => {
    if (val !== undefined && val !== null) {
      currentFileSizeMB.value = String(val); // ซิงค์ค่าเริ่มต้น
    }
  },
  { immediate: true }
);


const doAction = (data) => {
  if (data.label === "กล้องถ่ายรูป") {
    goCamera(); // ✅ เรียกใช้ฟังก์ชันเปิดกล้องด้วย getUserMedia
  } else if (data.label === "อัปโหลดรูป") {
    goUpload();
  }
};

const viewImage = () => {
  ImageModalRef.value.openModal(props.image);
};


const goCamera = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    alert("เบราว์เซอร์ของคุณไม่รองรับการเข้าถึงกล้อง");
    return;
  }
  
  showCropper.value = false; 

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: facingMode.value, 
      },
    });

    mediaStream = stream; // เก็บ stream ไว้เพื่อปิดภายหลัง
    showCamera.value = true;

    // รอให้ <video> ถูก render ก่อน
    await nextTick(); 
    const video = videoRef.value;
    if (video) {
        video.srcObject = stream;
        video.play(); // เริ่มเล่นวิดีโอ
    }
    
  } catch (error) {
    console.error("Error accessing camera:", error);
    alert("ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบการอนุญาตการใช้งานกล้อง");
  }
};

const closeCamera = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop()); // หยุดการทำงานของกล้อง
    mediaStream = null;
  }
  showCamera.value = false;
};

const takePicture = () => {
  const video = videoRef.value;
  if (!video) return;

  // 1. สร้าง Canvas ชั่วคราว
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  
  // 2. วาดภาพปัจจุบันของวิดีโอลงบน Canvas
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  // 3. แปลงภาพใน Canvas เป็น Blob (ไฟล์)
  canvas.toBlob((blob) => {
    if (!blob) {
      console.warn("ไม่สามารถแปลงภาพเป็น Blob ได้");
      return;
    }

    // 4. สร้าง File object จาก Blob
    const cameraFile = new File([blob], `camera_photo_${Date.now()}.jpeg`, {
      type: 'image/jpeg',
    });
    
    closeCamera(); // ปิดกล้อง

    // 5. เตรียมไฟล์สำหรับ Cropper
    rawImage.value = cameraFile;
    currentFileSizeMB.value = (cameraFile.size / 1048576).toFixed(2);

    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target.result;
      showCropper.value = true; // เปิด Cropper Modal
    };
    reader.readAsDataURL(cameraFile);
    
  }, "image/jpeg", 0.9); // บันทึกเป็น JPEG คุณภาพ 90%
};


// trigger upload
const goUpload = () => {
  // ปิด modal กล้องถ้าเปิดอยู่ก่อน
  closeCamera();
  fileInput.value?.click();
};

// file input change
const handleFileChange = (e) => {
  const file = e.target.files?.[0];
  e.target.value = ""; // reset input value for next upload of same file

  if (
    file &&
    /\.(jpe?g|png)$/i.test(file.name) &&
    ["image/jpeg", "image/png"].includes(file.type)
  ) {
    currentFileSizeMB.value = (file.size / 1048576).toFixed(2); // ปัดเศษ 2 ตำแหน่ง

    rawImage.value = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target.result;
      showCropper.value = true;
    };
    reader.readAsDataURL(file);
  }
};

// crop image
const cropImage = async () => {
  const result = cropperRef.value?.getResult?.();

  if (result?.canvas) {
    result.canvas.toBlob((blob) => {
      console.log("blob", blob);
      if (blob) {
        const croppedFile = new File([blob], rawImage.value.name, {
          type: blob.type,
        });

        const newSizeMB = (croppedFile.size / 1048576).toFixed(2);
        currentFileSizeMB.value = newSizeMB; // อัปเดตขนาดไฟล์ที่แสดงผล

        croppedImage.value = URL.createObjectURL(croppedFile);
        emit("update:image", croppedFile);
        showCropper.value = false;
      }
    }, "image/jpeg");
  } else {
    console.warn("ไม่สามารถตัดรูปได้");
  }
};

// remove image
const removeImage = () => {
  croppedImage.value = null;
  currentFileSizeMB.value = "0.00"; // ล้างขนาดไฟล์เมื่อลบรูป
  emit("update:image", null);
};
</script>

<style lang="scss" scoped>
.BodyS {
  color: #7c7379;

  /* Body/s */
  font-family: Kanit;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px; /* 150% */
  letter-spacing: 0.1px;
}
</style>